import { Box, Group, Text, Progress } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { FONT_WEIGHT_SEMIBOLD } from '@/constants/ui';
import type { ProgressSectionProps } from './ProgressSection.types';
import styles from './ProgressSection.module.css';

export const ProgressSection = ({
  title,
  completed,
  total,
  label,
  size = 'md',
  className,
}: ProgressSectionProps) => {
  const { t } = useTranslation();

  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  const detailsMargin = size === 'xs' ? 'xs' : 'sm';

  // When `label` is set it drives both cells: label on the left, merged count
  // on the right. Otherwise show the detailed completion text + percentage.
  const leftText = label ?? t('common.completedOf', { completed, total });
  const rightText = label
    ? t('common.progressCount', { completed, total, percentage })
    : `${percentage}%`;

  return (
    <Box className={clsx(styles.progressSection, className)}>
      {title && (
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={FONT_WEIGHT_SEMIBOLD}>
            {title}
          </Text>
        </Group>
      )}

      <Group justify="space-between" mb={detailsMargin}>
        <Text size="xs" c="dimmed">
          {leftText}
        </Text>
        <Text size="xs" c="dimmed">
          {rightText}
        </Text>
      </Group>

      <Progress
        value={percentage}
        size={size}
        color={percentage === 100 ? 'green' : 'indigo'}
        className={styles.progressBar}
      />
    </Box>
  );
};
